import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { type CalenderAppointmentsSearchFilters } from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import {
  type CoWorkerCalendarItem,
  type OutlookEvent,
} from "@/features/calendar/types";
import { getCalStartHour, mapToCalendarItem } from "@/features/calendar/utils";
import {
  getFirstDateOfWeek,
  getLastDateOfWeek,
} from "@/features/calendar/utils/dateUtils";
import { filterVisibleTasks } from "@/features/calendar/utils/filterVisibleTasks";
import { mapScheduleToActivity } from "@/features/calendar/work-schedule/getScheduleActivities";
import { getWorkhours } from "@/features/calendar/work-schedule/getWorkHours";
import { DndContext } from "@dnd-kit/core";
import styled from "@emotion/styled";
import { type Appointment, type Schedule, type Task } from "@repo/api";
import { LightMode } from "@repo/skapa";
import { hasItems } from "@repo/utils";
import {
  addDays,
  differenceInCalendarDays,
  differenceInMinutes,
  set,
} from "date-fns";
import { type RefObject, useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

import { useHandleDrag } from "../hooks/useHandleDrag";
import { DragPreviewProvider } from "../team-calendar/DragPreviewContext";
import { AllDayBlocks } from "./AllDayBlocks";
import { AppointmentBlocks } from "./AppointmentBlocks";
import { CalendarDay } from "./WeekCalendarDay";
import { WeekCurrentTimeLine } from "./WeekCurrentTimeLine";
import { WeekHours } from "./WeekHours";
import { WEEK_BLOCK_HEIGHT, WEEK_HOUR_TITLE_BLOCK_WIDTH } from "./constants";
import { getAllDayHeight } from "./utils";

const CalendarContainer = styled.div`
  background-color: ${LightMode.colourStaticWhite};

  display: flex;
  justify-content: center;
  position: relative;
`;

interface WeekCalendarItems {
  inCalendar: CoWorkerCalendarItem[];
  allDay: CoWorkerCalendarItem[];
  offHours: CoWorkerCalendarItem[];
}

const mapAppointmentsToWeekCalendar = (
  appointments: CoWorkerCalendarItem[],
  startHour: number
) => {
  return appointments.reduce(
    (memo, appointment) => {
      if (appointment.allDay) {
        memo.allDay.push(appointment);
      } else {
        const spanningDays = differenceInCalendarDays(
          appointment.end,
          appointment.start
        );

        memo.inCalendar.push(appointment);

        let newStartDate = appointment.start;
        for (let day = 0; day < spanningDays; day++) {
          newStartDate = set(addDays(newStartDate, 1), {
            hours: startHour,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });

          memo.inCalendar.push({
            ...appointment,
            displayStart: newStartDate,
            length: Math.max(
              differenceInMinutes(appointment.end, newStartDate),
              0
            ),
          });
        }
      }

      return memo;
    },
    {
      inCalendar: [],
      allDay: [],
      offHours: [],
    } as WeekCalendarItems
  );
};

export const WeekCalendar = ({
  items,
  appointmentFilters,
  outlook,
  schedules,
  openModal,
}: {
  items: (Appointment | Task | OutlookEvent)[];
  outlook: OutlookEvent[];
  schedules: Schedule[] | undefined;
  appointmentFilters: CalenderAppointmentsSearchFilters;
  openModal: (props: OpenModalProps) => void;
}) => {
  const { saveTask } = useCalendarContext();
  const squareRef = useRef<HTMLDivElement>(null);
  const { width = 0 } = useResizeObserver({
    ref: squareRef as RefObject<HTMLDivElement>,
  });

  const firstDayOfWeek = getFirstDateOfWeek(
    appointmentFilters.selectedDate,
    appointmentFilters.calendarSettings.firstDayOfWeek
  );
  const lastDayOfWeek = getLastDateOfWeek(
    appointmentFilters.selectedDate,
    appointmentFilters.calendarSettings.firstDayOfWeek
  );

  const workHours = getWorkhours([], "", false); // Disabled for now
  const filteredItems = filterVisibleTasks(items);

  const mappedItems = mapToCalendarItem(
    [...filteredItems, ...outlook],
    firstDayOfWeek,
    lastDayOfWeek
  );
  const selectedEmails = appointmentFilters.overviewCalendar.emailId;
  const scheduleActivities = (schedules ?? [])
    .filter(
      (s) =>
        !selectedEmails ||
        selectedEmails.length === 0 ||
        selectedEmails.some(
          (e) => e.toLowerCase() === s.coworkerEmail.toLowerCase()
        )
    )
    .map(mapScheduleToActivity)
    .filter((a) => a.start >= firstDayOfWeek && a.start <= lastDayOfWeek);
  const { calStartHour } = getCalStartHour([
    ...mappedItems,
    ...scheduleActivities,
  ]);
  const weekCalendarItems = mapAppointmentsToWeekCalendar(
    [...mappedItems, ...scheduleActivities],
    calStartHour
  );

  const allDayHeight = hasItems(weekCalendarItems.allDay)
    ? getAllDayHeight(weekCalendarItems.allDay, (15 * WEEK_BLOCK_HEIGHT) / 60)
    : 0;

  const weekBlockWidth = Math.round((width - WEEK_HOUR_TITLE_BLOCK_WIDTH) / 7);

  const days = [];
  const NO_OF_DAYS = 7;
  for (let i = 0; i < NO_OF_DAYS; i++) {
    const day = addDays(firstDayOfWeek, i);
    days.push(
      <CalendarDay
        key={day.getTime()}
        day={day}
        startHour={calStartHour}
        allDayHeight={allDayHeight}
        hasAllDay={hasItems(weekCalendarItems.allDay)}
        width={weekBlockWidth}
        openModal={openModal}
        workHours={workHours}
        isLastDay={i === NO_OF_DAYS - 1}
      />
    );
  }

  const showInitials =
    (appointmentFilters.overviewCalendar.emailId ?? []).length > 1;

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    snapToGridModifier,
    sensors,
    dragPreview,
  } = useHandleDrag({
    onUpdateTask: saveTask,
    weekBlockHeight: WEEK_BLOCK_HEIGHT,
    weekBlockWidth,
  });

  return (
    <CalendarContainer ref={squareRef}>
      <DndContext
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        sensors={sensors}
        modifiers={[snapToGridModifier]}
      >
        <DragPreviewProvider value={{ dragPreview }}>
          <WeekHours
            startHour={calStartHour}
            allDayHeight={allDayHeight}
            hasAllDay={hasItems(weekCalendarItems.allDay)}
            blockWidth={WEEK_HOUR_TITLE_BLOCK_WIDTH}
          />
          {days.map((d) => d)}
          <AllDayBlocks
            items={weekCalendarItems.allDay}
            blockWidth={weekBlockWidth}
            startDate={firstDayOfWeek}
            openModal={openModal}
            showInitials={showInitials}
          />
          <AppointmentBlocks
            items={weekCalendarItems.inCalendar}
            allDayHeight={allDayHeight}
            blockWidth={weekBlockWidth}
            startDate={firstDayOfWeek}
            openModal={openModal}
            showInitials={showInitials}
          />
          <WeekCurrentTimeLine
            allDayHeight={allDayHeight}
            startDate={firstDayOfWeek}
            startHour={calStartHour}
            endDate={lastDayOfWeek}
            blockWidth={weekBlockWidth}
          />
        </DragPreviewProvider>
      </DndContext>
    </CalendarContainer>
  );
};
